import _ from 'lodash';
import { Observer } from 'mobx-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  Badge,
  Button,
  Col,
  Popover,
  Row,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  customFleets,
  PitstopButton,
  prescriptiveMaintenanceMessages,
} from 'shared';

import {
  AppStore,
  CarStore,
  CurrentUserStore,
  IssueStore,
  ShopStore,
} from 'stores';
import { UserRoles } from 'stores/Classes/UserObject';

import {
  getRoutineDueEngineHours,
  getRoutineDueMileage,
} from 'containers/IssueProfile/share/utils';

import { calcMileage, convertSecondsToHours } from 'helpers/unitCalculations';
import { getPriorityTagColor } from 'stores/Classes/IssueObject';

import PitstopBadge from 'components/General/PitstopBadge';
import DeviceStore from 'stores/Classes/DeviceStore';
import { EventBus } from 'stores/EventBus';

import {
  ClockCircleOutlined,
  DashboardOutlined,
  DownOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  UpOutlined,
} from '@ant-design/icons';
import ExternalIcon from 'assets/icons/external.svg?react';
import { PitStopTagStatus } from 'shared/PitstopUI/PitStopTagStatus';
import { capitalizeWords, getColor, remains } from 'shared/pms';

const { Text } = Typography;

const CarIndicator = styled.div`
  width: 5px;
  position: absolute;
  background-color: ${(props) => props.color || 'initial'};
  height: 140%;
  top: -20px;
  padding: 0;
  left: -15px;
`;

const CarIDSection = styled.div`
  position: relative;

  p {
    margin-bottom: 0px;
  }
`;

const ScannerSection = styled.div`
  p {
    margin-bottom: 0px;
  }
`;

const IssueCategorySection = styled.span`
  white-space: pre-wrap;
`;

const NextPMWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const NextPMPercentageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .percentage {
    font-size: 20px;
  }
  .description {
    font-size: 12px;
  }
  .ant-tag {
    border-radius: 100px;
    background-color: white;
    margin-top: 5px;
    font-size: 12px;
  }
`;

const StyledAction = styled.span`
  max-width: 155px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const renderIndicator = (car) => {
  const priority = car.issueCounts;
  if (priority?.critical > 0) {
    return <CarIndicator color={getPriorityTagColor('Critical')} />;
  }
  if (priority?.major > 0) {
    return <CarIndicator color={getPriorityTagColor('Major')} />;
  }
  return <CarIndicator />;
};

export const renderSuggestedAction = (car) => {
  //maxPriority is returning wrong, so temporarily overriding it
  //const maxPriority = car.maxPriority;
  const maxPriority = car.issueCounts.critical > 0 ? '4' : '2';
  //TODO Find some way to make adjPriority accessible in the car object
  //const maxAdjPriority = car.maxAdjPriority;

  //maxMajorIssueDate is returning wrong, so temporarily overriding it
  //const maxMajorIssueDate = car.maxMajorIssueDate;
  const now = new Date();
  //Temp maxMajorIssueDate to always trigger the stale major message
  const maxMajorIssueDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 8
  );
  const routineServicePresent = car.routineServicePresent;
  const issueCounts = null; // car.issueCounts;
  return (
    <p>
      {prescriptiveMaintenanceMessages(
        maxPriority,
        maxMajorIssueDate,
        routineServicePresent,
        issueCounts
      )}
    </p>
  );
};

const highlightMatch = (text, search) => {
  // TO-DO: fix toLowerCase
  if (!search || typeof search !== 'string') return text;
  if (!text) return '';

  const index = text.toLowerCase().indexOf(search.toLowerCase());
  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + search.length);
  const after = text.slice(index + search.length);

  return (
    <>
      {before}
      <span style={{ backgroundColor: 'yellow' }}>{match}</span>
      {after}
    </>
  );
};

export const renderVehicleId = (car, searchValue) => {
  const unit = CurrentUserStore?.user?.displayedOdometer;
  const carNameText = car.carName || car.mmy;
  const vinText = car.vin ? car.vin : 'No VIN added';
  return (
    <CarIDSection>
      {renderIndicator(car)}
      <strong
        data-test="table-asset-column-vehicle-name"
        style={{ fontSize: '16px' }}
      >
        <Link to={`/car/${car.id}`}>
          {carNameText
            ? highlightMatch(carNameText, searchValue)
            : highlightMatch(vinText, searchValue)}
        </Link>
      </strong>
      <div style={{ fontSize: '12px' }}>
        {carNameText && highlightMatch(vinText, searchValue)}
        {car.meta?.additionalInfo && (
          <div>{highlightMatch(car.meta.additionalInfo, searchValue)}</div>
        )}
      </div>
      {car.carName && <p>{car.mmy}</p>}
      {customFleets.motiveFleets.includes(ShopStore.currentShop.id) ? (
        <></>
      ) : (
        <>
          <p style={{ fontSize: '14px' }}>
            {`${Number(calcMileage(car.totalMileage).toFixed(1)).toReadable()}
              ${unit === 'miles' ? 'mi' : unit === 'km' ? 'km' : unit ? 'km' : 'mi'}`}
          </p>
          {!customFleets.wolfline.includes(ShopStore.currentShop.id) &&
            !customFleets.bmc.includes(ShopStore.currentShop.id) && (
              <p style={{ fontSize: '14px' }}>
                {Math.round(
                  car?.getEngineHours
                    ? car?.getEngineHours()
                    : car.engineHours
                      ? Number(convertSecondsToHours(car.engineHours)).toFixed(
                          1
                        )
                      : 0
                ).toReadable()}{' '}
                hrs
              </p>
            )}
        </>
      )}
    </CarIDSection>
  );
};

const StyledBadge = ({ count, backgroundColor }) => (
  <Badge
    showZero={true}
    count={String(count)}
    style={{ backgroundColor }}
    overflowCount={1000000}
  />
);

StyledBadge.propTypes = {
  count: PropTypes.any,
  backgroundColor: PropTypes.string,
};

const StyledBadgeV2 = ({ style = {}, count, borderColor, color }) => (
  <Badge
    showZero={true}
    count={String(count)}
    style={{
      ...style,
      backgroundColor: '#ffffff',
      borderColor,
      color,
      boxShadow: '0 0 0 1px #d9d9d9 inset',
    }}
    overflowCount={1000000}
  />
);

StyledBadgeV2.propTypes = {
  style: PropTypes.any,
  count: PropTypes.any,
  borderColor: PropTypes.string,
  color: PropTypes.string,
};

export const renderIssues = (car) => {
  const criticalCount = car.issueCounts.critical || 0;
  const majorCount = car.issueCounts.major || 0;
  const minorCount = car.issueCounts.minor || 0;

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col xl={8} xs={6}>
          <StyledBadge
            count={criticalCount}
            backgroundColor={getPriorityTagColor('Critical')}
          />
        </Col>

        <Col xl={16} xs={18}>
          Critical
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col xl={8} xs={6}>
          <StyledBadge
            count={majorCount}
            backgroundColor={getPriorityTagColor('Major')}
          />
        </Col>

        <Col xl={16} xs={18}>
          Major
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col xl={8} xs={6}>
          <StyledBadge
            count={minorCount}
            backgroundColor={getPriorityTagColor('Minor')}
          />
        </Col>

        <Col xl={16} xs={18}>
          Minor
        </Col>
      </Row>
    </>
  );
};

export const renderIssuesV2 = (car) => {
  const criticalCount = car?.issueCounts?.critical || 0;
  const majorCount = car?.issueCounts?.major || 0;
  const minorCount = car?.issueCounts?.minor || 0;

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <StyledBadgeV2
            count={criticalCount}
            borderColor={getPriorityTagColor('Critical')}
            color={getPriorityTagColor('Critical')}
          />
          <StyledBadgeV2
            style={{ marginLeft: 5 }}
            count={majorCount}
            borderColor={getPriorityTagColor('Major')}
            color={getPriorityTagColor('Major')}
          />
          <StyledBadgeV2
            style={{ marginLeft: 5 }}
            count={minorCount}
            borderColor={getPriorityTagColor('Minor')}
            color={getPriorityTagColor('Minor')}
          />
        </Col>
      </Row>
    </>
  );
};

export const renderIssuesWithShowIssuesLink = (
  car,
  onExpandClick = () => {},
  currentExpandedCarIds = []
) => {
  const criticalCount = car.issueCounts.critical || 0;
  const majorCount = car.issueCounts.major || 0;
  const minorCount = car.issueCounts.minor || 0;

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <StyledBadgeV2
            count={criticalCount}
            borderColor={getPriorityTagColor('Critical')}
            color={getPriorityTagColor('Critical')}
          />
          <StyledBadgeV2
            style={{ marginLeft: 5 }}
            count={majorCount}
            borderColor={getPriorityTagColor('Major')}
            color={getPriorityTagColor('Major')}
          />
          <StyledBadgeV2
            style={{ marginLeft: 5 }}
            count={minorCount}
            borderColor={getPriorityTagColor('Minor')}
            color={getPriorityTagColor('Minor')}
          />
        </Col>
        <Col span={24}>
          <Button type="link" onClick={() => onExpandClick(car, 'issue')}>
            {currentExpandedCarIds.indexOf(car.id) > -1 ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            )}
            {currentExpandedCarIds.indexOf(car.id) > -1
              ? 'Hide Details'
              : 'Show Issues'}
            {currentExpandedCarIds.indexOf(car.id) > -1 ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            )}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export const renderIssuesCat = (car) => {
  const categories = car.issues.active.data
    .concat(car.issues.upcoming.data)
    .reduce((prev, id) => {
      if (IssueStore.data.has(id)) {
        const issueDetail = IssueStore.data.get(id).issueDetail;
        if (
          prev.length < 3 &&
          issueDetail &&
          issueDetail.item &&
          issueDetail.item !== 'unknown' &&
          issueDetail.item.length > 0
        ) {
          prev.push(issueDetail.item);
        }
        if (prev.length === 3) {
          prev.push('...');
        }
      }
      return prev;
    }, []);
  return <IssueCategorySection>{categories.join(', ')}</IssueCategorySection>;
};

export const renderScanner = (scanner, car) => {
  const scannerId = _.get(scanner, 'scannerId');
  const lastConnectionTimestamp = _.get(car, 'lastConnectionTimestamp');
  let timestampText = 'Check device connection';
  let monthDiff = 0;

  if (lastConnectionTimestamp) {
    const status = moment
      .tz(
        lastConnectionTimestamp * 1000,
        CurrentUserStore.user.settings.timezone
      )
      .fromNow();

    monthDiff = moment().diff(
      moment.tz(
        lastConnectionTimestamp * 1000,
        CurrentUserStore.user.settings.timezone
      ),
      'M'
    );

    timestampText = `Last Active ${status}`;
  }

  if (
    (scannerId && lastConnectionTimestamp && monthDiff < 1) ||
    _.isBoolean(CarStore.demo)
  ) {
    return (
      <ScannerSection>
        <Badge status="success" text="Active" />
        <p>{scannerId}</p>
        <p>{timestampText}</p>
      </ScannerSection>
    );
  } else if (
    (scannerId && !lastConnectionTimestamp) ||
    (scannerId && lastConnectionTimestamp && monthDiff >= 1)
  ) {
    return (
      <ScannerSection>
        <Badge status="warning" text="Inactive" />
        <p>{scannerId}</p>
        <p>{timestampText}</p>
      </ScannerSection>
    );
  } else if (!scannerId && lastConnectionTimestamp) {
    return (
      <ScannerSection>
        <Badge status="error" text="Disconnected" />
        <p>{timestampText}</p>
      </ScannerSection>
    );
  } else {
    return (
      <ScannerSection>
        <Badge status="error" text="No Device" />
      </ScannerSection>
    );
  }
};

export const renderDriver =
  (view, editable = true) =>
  (carObj) => {
    return (
      <Observer>
        {() => {
          const car = CarStore.data.get(carObj.id);

          let displayButton = (
            <PitstopButton
              style={{ width: '100%' }}
              onClick={() => {
                view.setState({ selectedCarId: car.id }, () => {
                  AppStore.openModals.set(view.modalId, true);
                });
              }}
            >
              <EditOutlined />
              {car.user ? 'Edit' : 'Assign'}
            </PitstopButton>
          );

          let displayProfileLink = <></>;

          if (car.user) {
            displayProfileLink = (
              <div style={{ textAlign: 'center' }}>
                <Link to={`/user/${car.user.id}`}>{car.user.name}</Link>
              </div>
            );
          }

          if (!editable || CurrentUserStore.user.role === UserRoles.customer) {
            displayButton = <></>;
          }

          return (
            <>
              <Col span={24}>{displayButton}</Col>
              <Col span={24}>{displayProfileLink}</Col>
            </>
          );
        }}
      </Observer>
    );
  };

export const renderNextPMVisit = (nextPmCar) => {
  return (
    <Observer>
      {() => {
        if (!nextPmCar || !nextPmCar.nextPm) {
          return (
            <>
              <Spin />
            </>
          );
        }
        if (_.isEmpty(nextPmCar.nextPm)) {
          return (
            <>
              <Col span={24}>
                <Link
                  to={`/service-interval-new?carId=${nextPmCar.id}&carType=${nextPmCar.carType}`}
                >
                  <EditOutlined />
                  <span className="marginLeft5">Setup PM</span>
                </Link>
              </Col>
            </>
          );
        }
        const dueMessages = [];
        for (const nextPm of nextPmCar.nextPm) {
          if (getRoutineDueEngineHours(nextPm))
            dueMessages.push(
              <>
                <b>On Engine Hours:</b>{' '}
                {`${Number(getRoutineDueEngineHours(nextPm)).toReadable()}`}{' '}
                engine hours
              </>
            );

          if (getRoutineDueMileage(nextPm))
            dueMessages.push(
              <>
                <b>On Mileage: </b>{' '}
                {`${Number(getRoutineDueMileage(nextPm)).toReadable()} ${
                  CurrentUserStore.user.displayedOdometer
                }`}
              </>
            );
        }
        if (dueMessages.length === 0) {
          return (
            <>
              <Col span={24}>
                <Link
                  to={`/service-interval-new?carId=${nextPmCar.id}&carType=${nextPmCar.carType}`}
                >
                  <EditOutlined />
                  <span className="marginLeft5">Setup PM</span>
                </Link>
              </Col>
            </>
          );
        }
        return (
          <>
            <Col span={24}>
              {_.map(dueMessages, (msg) => (
                <>
                  {msg}
                  <br />
                </>
              ))}
            </Col>
          </>
        );
      }}
    </Observer>
  );
};

export const renderNextPMDueInPercentage = (
  car,
  onExpandClick = () => {},
  currentExpandedCarIds = []
) => {
  const { device } = DeviceStore;
  const {
    due_engine_hours,
    due_mileage,
    due_time_in_s,
    rule_type,
    display_type,
    pm_percent,
    pm_status,
    engineHours,
    totalMileage,
    pm_action,
  } = car;
  if (pm_percent === null || pm_percent === undefined) {
    return (
      <>
        {CurrentUserStore.isTechnician ? (
          <>--</>
        ) : (
          <Col span={24}>
            <Link
              to={`/service-interval-new?carId=${car.id}&carType=${car.carType}`}
            >
              <EditOutlined />
              <span className="marginLeft5">Setup PM</span>
            </Link>
          </Col>
        )}
      </>
    );
  }

  const userSettings = CurrentUserStore.user.settings;
  const odometer = userSettings?.odometer === 'km' ? 'km' : 'mi';

  const carRemains = remains({
    dueTime: due_time_in_s,
    safetyInspection: false,
    ruleType: rule_type,
    dueEngineHours: due_engine_hours,
    engineHours: engineHours,
    dueMileage: due_mileage,
    totalMileage: totalMileage,
    odometer,
    displayType: display_type,
  });

  const style = {
    color: getColor(pm_status),
  };
  return (
    <>
      <Col span={24}>
        <NextPMPercentageWrapper style={style}>
          <span className="percentage" style={style}>
            {Math.max(0, Math.floor(pm_percent))}%
          </span>
          <Tooltip placement="top" title={pm_action}>
            <StyledAction className="description" style={style}>
              {pm_action}
            </StyledAction>
          </Tooltip>
          <span className="description" style={style}>
            {`${
              pm_status === 'No action needed'
                ? 'On Track'
                : capitalizeWords(pm_status)
            } ${
              display_type.display === 'time'
                ? moment(new Date(due_time_in_s * 1000)).format('MM/DD/YYYY')
                : ''
            }`}
          </span>
          {carRemains.map((remain, index) => (
            <span className="description" key={index}>
              {pm_status === 'Overdue' ? 'By' : 'In'} {remain.displayValue}{' '}
              {remain.displayText}
            </span>
          ))}
        </NextPMPercentageWrapper>
      </Col>
      {device !== 'mobile' && (
        <Col span={24}>
          <Button type="link" onClick={() => onExpandClick(car, 'pm')}>
            {currentExpandedCarIds.indexOf(car.id) > -1 ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            )}
            {currentExpandedCarIds.indexOf(car.id) > -1
              ? 'Hide Details'
              : 'Show PMs'}
            {currentExpandedCarIds.indexOf(car.id) > -1 ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            )}
          </Button>
        </Col>
      )}
    </>
  );
};

export const renderNextPMVisitV2 = ({
  car = {},
  nextPm = null,
  engineHours,
  totalMileage,
}) => {
  return (
    <Observer>
      {() => {
        if (!nextPm || !nextPm?.nextPm) {
          return (
            <>
              <Spin />
            </>
          );
        }
        if (_.isEmpty(nextPm?.nextPm)) {
          return (
            <>
              <Col span={24}>
                <Link
                  to={`/service-interval-new?carId=${car.id}&carType=${car.carType}`}
                >
                  <EditOutlined />
                  <span className="marginLeft5">Setup PM</span>
                </Link>
              </Col>
            </>
          );
        }
        const dueMessages = [];
        for (const currentNextPm of nextPm?.nextPm || []) {
          if (getRoutineDueEngineHours(currentNextPm)) {
            const routineDueEngineHours = Number(
              getRoutineDueEngineHours(currentNextPm)
            );
            const engineHoursInHours = convertSecondsToHours(engineHours);
            const dueSoon = engineHoursInHours < routineDueEngineHours;
            if (dueSoon) {
              dueMessages.push({
                message: (
                  <>
                    <Text>
                      <ClockCircleOutlined style={{ marginRight: '0.5rem' }} />
                      In{' '}
                      {Math.round(
                        Number(routineDueEngineHours - engineHoursInHours)
                      )}{' '}
                      Engine Hours
                    </Text>
                  </>
                ),
                dueSoon,
              });
            } else {
              // overdue
              dueMessages.push({
                message: (
                  <>
                    <Text type="danger">
                      <ExclamationCircleOutlined
                        style={{ marginRight: '0.5rem' }}
                      />
                      Overdue by{' '}
                      {Math.round(
                        Number(engineHoursInHours - routineDueEngineHours)
                      )}{' '}
                      Engine Hours
                    </Text>
                  </>
                ),
                dueSoon,
              });
            }
          }

          if (getRoutineDueMileage(currentNextPm)) {
            const dueSoon =
              calcMileage(totalMileage) < getRoutineDueMileage(currentNextPm);
            if (dueSoon) {
              dueMessages.push({
                message: (
                  <>
                    <Text>
                      <DashboardOutlined style={{ marginRight: '0.5rem' }} />
                      Due in{' '}
                      {Math.round(
                        Number(
                          getRoutineDueMileage(currentNextPm) -
                            calcMileage(totalMileage)
                        )
                      )}{' '}
                      {CurrentUserStore.user.displayedOdometer}
                    </Text>
                  </>
                ),
                dueSoon,
              });
            } else {
              // overdue
              dueMessages.push({
                message: (
                  <>
                    <Text type="danger">
                      <ExclamationCircleOutlined
                        style={{ marginRight: '0.5rem' }}
                      />
                      Overdue by{' '}
                      {Math.round(
                        Number(
                          calcMileage(totalMileage) -
                            getRoutineDueMileage(currentNextPm)
                        )
                      )}{' '}
                      {CurrentUserStore.user.displayedOdometer}
                    </Text>
                  </>
                ),
                dueSoon,
              });
            }
          }
        }
        if (dueMessages.length === 0) {
          return (
            <>
              <Col span={24}>
                <Link
                  to={`/service-interval-new?carId=${car.id}&carType=${car.carType}`}
                >
                  <EditOutlined />
                  <span className="marginLeft5">Setup PM</span>
                </Link>
              </Col>
            </>
          );
        }
        return (
          <>
            <Col span={24}>
              <NextPMWrapper>
                <Text strong>Routine Service</Text>
                {_.map(dueMessages, (msg) => (
                  <>
                    {msg.message}
                    <Tag key={msg.message} color={msg.dueSoon ? 'blue' : 'red'}>
                      {msg.dueSoon ? 'Due Soon' : 'Overdue'}
                    </Tag>
                  </>
                ))}
              </NextPMWrapper>
            </Col>
          </>
        );
      }}
    </Observer>
  );
};

renderNextPMVisitV2.propTypes = {
  car: PropTypes.any,
  nextPm: PropTypes.any,
  engineHours: PropTypes.any,
  totalMileage: PropTypes.any,
};

export const renderWorkOrderAssigned = (
  car = {},
  shopWorkOrderStatuses = [],
  onExpandClick = () => {},
  currentExpandedCarIds = []
) => {
  const { device } = DeviceStore;
  const { shopUsers } = ShopStore;

  const workOrdersForCar = _.get(car, 'workOrdersAssigned', []);

  workOrdersForCar.sort((a, b) =>
    moment(b.created_at).diff(moment(a.created_at))
  );

  const getWorkOrderStatusObj = (status) => {
    const obj = shopWorkOrderStatuses.find((s) => s.key === status);
    return obj || { name: status, color: '#000000' };
  };

  return (
    <Observer>
      {() => {
        if (!workOrdersForCar) {
          return (
            <>
              <Spin />
            </>
          );
        }
        if (_.isEmpty(workOrdersForCar)) {
          return (
            <>
              <Col span={24}>N/A</Col>
            </>
          );
        }

        // Always display the first work order
        const firstWorkOrder = workOrdersForCar[0];
        const {
          status: firstWorkOrderStatus,
          assigned_to: firstWorkOrderAssignedToId,
        } = firstWorkOrder;
        let orderNumber = firstWorkOrder.invoice_number;
        if (firstWorkOrder.w_ord_id) {
          orderNumber = firstWorkOrder.w_ord_id;
        }
        const assignedToUser = shopUsers.find(
          (user) => user.id === firstWorkOrderAssignedToId
        );
        const isExpanded = currentExpandedCarIds.includes(car.id);

        return (
          <>
            <Col span={24}>
              {device === 'mobile' ? (
                <>
                  <div
                    onClick={() => {
                      EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
                        type: 'work-order',
                        data: firstWorkOrder,
                        title: `Work Order #${orderNumber}`,
                        selectedCarId: firstWorkOrder.id_car,
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Row type="flex" justify="space-between" align="middle">
                      <Col>
                        <Text strong style={{ marginRight: 5 }}>
                          #{orderNumber}
                        </Text>
                        <PitStopTagStatus
                          content={
                            <Text
                              style={{
                                color: getWorkOrderStatusObj(
                                  firstWorkOrder.status
                                ).color,
                              }}
                            >
                              {getWorkOrderStatusObj(firstWorkOrderStatus).name}
                            </Text>
                          }
                        ></PitStopTagStatus>
                      </Col>
                      <Col>
                        <Row type="flex" align="middle">
                          <ExternalIcon />
                          <span className="marginLeft5">View</span>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div
                    onClick={() => {
                      EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
                        type: 'work-order',
                        data: firstWorkOrder,
                        title: `Work Order #${orderNumber}`,
                        selectedCarId: firstWorkOrder.id_car,
                      });
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <b style={{ color: '#1677ff' }}>#{orderNumber}</b>
                    <ExternalIcon style={{ width: '12px', height: '12px' }} />
                  </div>
                  <br />
                  <span
                    style={{
                      color: getWorkOrderStatusObj(firstWorkOrderStatus).color,
                      fontSize: '13px',
                      fontStyle: 'italic',
                    }}
                  >
                    {getWorkOrderStatusObj(firstWorkOrderStatus).name}
                  </span>
                  {assignedToUser && (
                    <>
                      <br />
                      <span>{assignedToUser.getFullName()}</span>
                    </>
                  )}
                </div>
              )}
            </Col>

            {/* Show Orders button if there are more work orders */}
            {workOrdersForCar.length > 1 && (
              <Col span={24} style={{ textAlign: 'center', marginTop: '4px' }}>
                <Button
                  type="link"
                  onClick={() => onExpandClick(firstWorkOrder.id_car)}
                >
                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                  {isExpanded ? 'Hide Orders' : 'Show Orders'}
                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                </Button>
              </Col>
            )}
          </>
        );
      }}
    </Observer>
  );
};

renderWorkOrderAssigned.propTypes = {
  car: PropTypes.any,
  shopWorkOrderStatuses: PropTypes.array,
  onExpandClick: PropTypes.func,
  currentExpandedCarIds: PropTypes.array,
};

export const renderAssetSafetyInspections = (
  car,
  onExpandClick = () => {},
  currentExpandedCarIds = []
) => {
  const { device } = DeviceStore;
  const {
    safety_due_time_in_s,
    safety_pm_percent,
    safety_pm_status,
    safety_due_mileage,
    safety_due_engine_hours,
    safety_rule_type,
    totalMileage,
    engineHours,
  } = car;

  const userSettings = CurrentUserStore.user.settings;
  const odometer = userSettings?.odometer === 'km' ? 'km' : 'mi';

  const getRemainingValue = () => {
    if (safety_rule_type === 'time' && safety_due_time_in_s) {
      const dueInDays = moment
        .unix(safety_due_time_in_s)
        .diff(moment(), 'days');
      const overdueDays = moment().diff(
        moment.unix(safety_due_time_in_s),
        'days'
      );
      return {
        value: overdueDays > 0 ? overdueDays : dueInDays,
        text: 'day(s)',
        dueDate: moment(new Date(safety_due_time_in_s * 1000)).format(
          'MM/DD/YYYY'
        ),
      };
    } else if (safety_rule_type === 'mileage' && safety_due_mileage) {
      const remaining = Math.abs(
        Number(safety_due_mileage) - Number(totalMileage)
      );
      return {
        value:
          userSettings?.odometer === 'km'
            ? remaining
            : Math.ceil(remaining / 1.60934),
        text: odometer,
        dueDate: null,
      };
    } else if (safety_rule_type === 'engine_hours' && safety_due_engine_hours) {
      return {
        value: Math.ceil(
          Math.abs(
            (Number(safety_due_engine_hours) - Number(engineHours)) / 3600
          )
        ),
        text: 'engine hours',
        dueDate: null,
      };
    }
    return null;
  };

  const remaining = getRemainingValue();

  const getColor = () => {
    switch (safety_pm_status) {
      case 'Overdue':
        return '#FF202F';
      case 'Due Soon':
        return '#FF812A';
      case 'No action needed':
      case 'In Compliance':
        return '#52c41a';
      default:
        return '#333333';
    }
  };

  const getIcon = () => {
    switch (safety_pm_status) {
      case 'Overdue':
        return 'alert';
      case 'Due Soon':
        return 'calendar';
      case 'Upcoming':
        return 'clock-circle';
      case 'In Compliance':
      case 'No action needed':
        return 'check-circle';
      default:
        return 'minus-circle';
    }
  };

  const getDueDateInText = () => {
    switch (safety_pm_status) {
      case 'Overdue':
        return 'Overdue';
      case 'Due Soon':
        return 'Due Soon';
      case 'Upcoming':
        return 'Due';
      default:
        return 'Due';
    }
  };

  const getDueInText = () => {
    switch (safety_pm_status) {
      case 'Overdue':
        return 'By';
      case 'Due Soon':
      case 'Upcoming':
      default:
        return 'In';
    }
  };

  const getBadgeText = () => {
    switch (safety_pm_status) {
      case 'Overdue':
        return 'Overdue';
      case 'Due Soon':
        return 'Due Soon';
      case 'Upcoming':
        return 'Upcoming';
      case 'No action needed':
      case 'In Compliance':
        return 'In Compliance';
      default:
        return '';
    }
  };

  // Return early if no safety inspection data is present
  if (!safety_pm_percent && !safety_pm_status && !remaining) {
    return (
      <div
        style={{
          width: '100%',
          textAlign: 'center',
        }}
      >
        --
      </div>
    );
  }

  return (
    <>
      <Row>
        <Col
          span={24}
          style={{
            textAlign: 'center',
          }}
        >
          <Typography.Text
            style={{
              color: getColor(),
              fontSize: '20px',
            }}
          >
            {Math.max(0, Math.floor(safety_pm_percent || 0))}
            {'%'}
          </Typography.Text>
        </Col>
        <Col
          span={24}
          style={{
            textAlign: 'center',
          }}
        >
          <Typography.Text
            style={{
              fontSize: '12px',
              color: getColor(),
            }}
          >
            {remaining && (
              <>
                <div>{`${getDueDateInText()} ${remaining.dueDate || ''}`}</div>
                {safety_pm_status !== 'In Compliance' && (
                  <div>
                    {getDueInText()} {remaining.value} {remaining.text}
                  </div>
                )}
              </>
            )}
          </Typography.Text>
        </Col>
        {safety_pm_status === 'In Compliance' && (
          <Col
            span={24}
            style={{
              textAlign: 'center',
              marginTop: '5px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <PitstopBadge
              color={getColor()}
              icon={getIcon()}
              text={getBadgeText()}
            />
          </Col>
        )}
      </Row>
      <Row>
        {device !== 'mobile' && (
          <Col
            span={24}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button type="link" onClick={() => onExpandClick(car, 'pm')}>
              {currentExpandedCarIds.indexOf(car.id) > -1 ? (
                <UpOutlined />
              ) : (
                <DownOutlined />
              )}
              {currentExpandedCarIds.indexOf(car.id) > -1
                ? 'Hide Details'
                : 'Show Inspections'}
              {currentExpandedCarIds.indexOf(car.id) > -1 ? (
                <UpOutlined />
              ) : (
                <DownOutlined />
              )}
            </Button>
          </Col>
        )}
      </Row>
    </>
  );
};

renderAssetSafetyInspections.propTypes = {
  car: PropTypes.any,
};
